/**
 * Which engagement you are in — and, when there is more than one, the way out
 * of it (IMPL-ENGAGE-002 §8.3).
 *
 * ┌─ IT USED TO SAY "SWITCH ENGAGEMENT" AND NAME NOTHING. ───────────────────┐
 * │ A verb with no subject. The owner could not tell from any screen which   │
 * │ organisation they were answering for, which matters most in exactly the  │
 * │ case the control exists for: a person on several engagements, moving     │
 * │ between them, answering scoping questions that are wrong if they are     │
 * │ answered against the wrong one.                                          │
 * │                                                                          │
 * │ Worse, it rendered NOTHING for a person on one engagement — most people  │
 * │ — on the reasoning that a control with no second option is dead chrome.  │
 * │ True of a SWITCHER. This is not only a switcher: naming where you are is │
 * │ worth a line on its own, and the sole-engagement case simply loses the   │
 * │ menu rather than the label.                                              │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Mounted only where a customer session can exist (`Shell` checks the viewer),
 * so the fetch below never fires for an anonymous visitor on the sign-in form.
 */
import { useEffect, useState } from 'react'

import type { ReachableEngagement } from '@nimer/contracts/person'

import { EngagementActions } from './EngagementActions.tsx'
import { orderRows } from './rowOrder.ts'
import { fetchReachableEngagements, selectEngagement } from './personApi.ts'
import { t } from '../shell/i18n.ts'
import './person.css'

/**
 * A full reload rather than a client-side navigation.
 *
 * The session cookie has just been rebound to a different engagement, and
 * every cache in the tree — answers, classification, the wizard's section
 * state — belongs to the previous one. Reloading is the honest way to drop all
 * of it at once; re-fetching each cache in place would leave any it missed
 * showing another engagement's content.
 */
function reloadInto(path: string): void {
  window.location.assign(path)
}

/**
 * The reachable set, or an empty one.
 *
 * A control that cannot load its list is a control that is not shown: there is
 * no error worth putting in the header, because the person's current
 * engagement is unaffected and every screen still works without it.
 */
function useReachable(): readonly ReachableEngagement[] {
  const [rows, setRows] = useState<readonly ReachableEngagement[]>([])

  useEffect(() => {
    let cancelled = false
    void fetchReachableEngagements()
      .then((reachable) => {
        if (!cancelled) setRows(orderRows(reachable))
      })
      .catch(() => {
        if (!cancelled) setRows([])
      })

    return () => {
      cancelled = true
    }
  }, [])

  return rows
}

function SwitcherItem({
  row,
  busy,
  onChoose,
}: {
  readonly row: ReachableEngagement
  readonly busy: boolean
  readonly onChoose: () => void
}) {
  return (
    <li role="none">
      <button
        type="button"
        role="option"
        // The one you are on is marked, and cannot be chosen again: rebinding
        // to the engagement you already hold costs a full reload and changes
        // nothing, which reads as the control being broken.
        aria-selected={row.is_current}
        className={`person-switcher__item${row.is_current ? ' person-switcher__item--on' : ''}`}
        disabled={busy || row.is_current}
        onClick={onChoose}
      >
        <span className="person-switcher__org">{row.org_name}</span>
        <span className="person-switcher__ref">{row.customer_ref}</span>
        {row.is_current ? (
          <span className="person-switcher__here">{t('chooser.youAreHere')}</span>
        ) : null}
        {row.open_for_me > 0 && !row.is_current ? (
          <span className="person-switcher__badge">
            {t('chooser.waitingOnYou', { count: row.open_for_me })}
          </span>
        ) : null}
      </button>
    </li>
  )
}

/**
 * Rebinding the session, and the disabled state while it is in flight.
 *
 * An unreachable reference simply releases the busy state — the row came from
 * this person's own reachable set, so a `false` here means it stopped being
 * reachable (revoked, or closed) between the fetch and the click. Re-rendering
 * the menu unchanged is the honest response; the next load drops the row.
 */
function useChoose(reload: (path: string) => void) {
  const [busy, setBusy] = useState(false)

  const choose = (customerRef: string): void => {
    setBusy(true)
    void selectEngagement(customerRef)
      .then((bound) => {
        if (bound) reload('/questionnaire')
        else setBusy(false)
      })
      .catch(() => {
        setBusy(false)
      })
  }

  return { busy, choose }
}

/**
 * The label: what this is, then which one you are on.
 *
 * The kicker is not decoration. Without it the header reads as a company name
 * floating in the chrome, which is how a customer portal usually announces
 * whose portal it is — the opposite of what this says.
 */
function CurrentLabel({ current }: { readonly current: ReachableEngagement | null }) {
  return (
    <span className="person-switcher__current">
      <span className="person-switcher__kicker">{t('chooser.workingOn')}</span>
      <span className="person-switcher__name">
        {current === null ? t('chooser.unknownEngagement') : current.org_name}
      </span>
      {current === null ? null : (
        <span className="person-switcher__current-ref">{current.customer_ref}</span>
      )}
    </span>
  )
}

/** Split out on the 50-line function gate. */
function SwitcherMenu({
  rows,
  busy,
  choose,
}: {
  readonly rows: readonly ReachableEngagement[]
  readonly busy: boolean
  readonly choose: (customerRef: string) => void
}) {
  return (
    <ul className="person-switcher__menu" role="listbox" aria-label={t('chooser.heading')}>
      {rows.map((row) => (
        <SwitcherItem
          key={row.customer_ref}
          row={row}
          busy={busy}
          onChoose={() => {
            choose(row.customer_ref)
          }}
        />
      ))}
    </ul>
  )
}

export function EngagementSwitcher({
  reload = reloadInto,
}: {
  readonly reload?: (path: string) => void
}) {
  const rows = useReachable()
  const [open, setOpen] = useState(false)
  const { busy, choose } = useChoose(reload)

  // Before the list arrives there is nothing true to say. A skeleton naming no
  // engagement would be a claim about which one you are on.
  if (rows.length === 0) return null
  const current = rows.find((row) => row.is_current) ?? null

  if (rows.length === 1) {
    return (
      <div className="person-switcher person-switcher--sole">
        <CurrentLabel current={current} />
        <EngagementActions current={current} />
      </div>
    )
  }

  return (
    <div className="person-switcher">
      <button
        type="button"
        className="person-switcher__toggle"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => {
          setOpen(!open)
        }}
      >
        <CurrentLabel current={current} />
        <span className="person-switcher__action">{t('chooser.switchEngagement')}</span>
      </button>
      {open ? <SwitcherMenu rows={rows} busy={busy} choose={choose} /> : null}
      <EngagementActions current={current} />
    </div>
  )
}
